import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Ban,
  ClipboardList,
  History,
  MapPin,
  Printer,
  SendHorizontal,
  StickyNote,
  Tag,
  Undo2,
} from 'lucide-react';
import {
  approveTask,
  breakTaskSignatures,
  changeVisitType,
  getTaskSignatures,
  getVisit,
  getVisitPdf,
  getVisitTypeChangeOptions,
  markVisitAsMissed,
  returnTaskToClinician,
  signTask,
  submitTaskWithoutSignature,
  unmarkVisitAsMissed,
  updateTaskNotes,
} from '../../services/api';
import { getVisitEvvReadingsVisitsVisitIdEvvGet as getVisitEvv } from '../../services/api/sdk.gen';
import type {
  MissedVisitDataCreate,
  TaskSignatureResponse,
  VisitResponse,
  VisitTypeId,
  VisitTypeOption,
} from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { usePdfExport, type PdfSource } from '../../hooks/usePdfExport';
import { useToast } from '../../hooks/useToastContext';
import type { TaskHeaderSecondaryAction } from '../task/TaskHeaderControls';
import {
  resolveAttestationFamily,
  selectAttestationForFamily,
  type ActiveAttestationsMap,
  type SigningAttestation,
} from '../signing/attestations';
import type {
  ESignatureCredentialInput,
  SignatureDateInput,
} from '../signing/esignatureCredentialApi';
import { useSignatureBypassAction } from '../signing/useSignatureBypassAction';
import { changeVisitTypeErrorCopy } from './visitTypeChangeCopy';
import { socConflictCopyFromError } from './socConflictCopy';

/**
 * Defer dialog open until the next animation frame so a closing Actions
 * dropdown releases its body pointer-events lock first. Without this, stacked
 * modals can leave the page unclickable.
 */
function useDeferredDialogOpener() {
  const framesRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    return () => {
      for (const frameId of framesRef.current.values()) {
        cancelAnimationFrame(frameId);
      }
      framesRef.current.clear();
    };
  }, []);

  return useCallback((key: string, open: () => void) => {
    const previous = framesRef.current.get(key);
    if (previous !== undefined) {
      cancelAnimationFrame(previous);
    }
    const frameId = requestAnimationFrame(() => {
      framesRef.current.delete(key);
      open();
    });
    framesRef.current.set(key, frameId);
  }, []);
}

export interface UseVisitDetailActionsArgs {
  visitId: string;
  visit: VisitResponse | null;
  isDeleted: boolean;
  isEditable: boolean;
  isMissedVisit: boolean;
  canBypassSignature: boolean;
  canSubmitWithoutSignature: boolean;
  canReturnToClinician: boolean;
  canViewWorkLog: boolean;
  canViewEvv: boolean;
  hasStagedAnswers: boolean;
  taskNotes: string;
  setTaskNotes: React.Dispatch<React.SetStateAction<string>>;
  setVisit: React.Dispatch<React.SetStateAction<VisitResponse | null>>;
  setSignatures: (signatures: TaskSignatureResponse[]) => void;
  setSaveError: React.Dispatch<React.SetStateAction<string | null>>;
  onVisitUpdated?: (visit: VisitResponse) => void;
  attestations: ActiveAttestationsMap | null | undefined;
}

export interface UseVisitDetailActionsResult {
  visitAttestation: SigningAttestation | null;
  signingLoading: boolean;
  approvingLoading: boolean;
  editingLoading: boolean;
  unsignedMoveLoading: boolean;
  markMissedLoading: boolean;
  unmarkMissedLoading: boolean;
  changeTypeLoading: boolean;
  taskNotesLoading: boolean;
  exportingId: string | null;

  signAttestationOpen: boolean;
  setSignAttestationOpen: (open: boolean) => void;
  approveAttestationOpen: boolean;
  setApproveAttestationOpen: (open: boolean) => void;
  showNotesDialog: boolean;
  setShowNotesDialog: (open: boolean) => void;
  showMarkMissedDialog: boolean;
  setShowMarkMissedDialog: (open: boolean) => void;
  showChangeTypeDialog: boolean;
  setShowChangeTypeDialog: (open: boolean) => void;
  showTransitionNotesModal: boolean;
  historyOpen: boolean;
  setHistoryOpen: (open: boolean) => void;
  workLogOpen: boolean;
  setWorkLogOpen: (open: boolean) => void;
  evvOpen: boolean;
  setEvvOpen: (open: boolean) => void;
  hasEvv: boolean;
  refreshEvvIndicator: () => void;

  visitTypeOptions: VisitTypeOption[];
  selectedVisitType: string;
  setSelectedVisitType: (visitTypeId: string) => void;
  draftNotes: string;
  setDraftNotes: (notes: string) => void;
  transitionNotes: string;
  setTransitionNotes: (notes: string) => void;

  handleSignAndSubmit: () => void;
  doSignAndSubmit: (
    credential: ESignatureCredentialInput,
    signatureDate?: SignatureDateInput
  ) => Promise<string | null>;
  handleApprove: () => void;
  doApprove: (credential: ESignatureCredentialInput) => Promise<string | null>;
  handleBreakSignatures: () => void;
  handleSubmitWithoutSignature: () => Promise<void>;
  handleReturnToClinician: () => Promise<void>;
  handleMarkAsMissed: (payload: MissedVisitDataCreate) => Promise<void>;
  handleUnmarkMissed: () => Promise<void>;
  handleChangeVisitType: () => Promise<void>;
  handleExportPdf: () => void;
  handleSaveNotes: () => Promise<void>;
  handleConfirmTransition: () => Promise<void>;
  handleCancelTransition: () => void;

  bypassDialog: React.ReactNode;
  secondaryHeaderActions: TaskHeaderSecondaryAction[];
}

/**
 * Workflow actions for VisitDetailPanel: sign, approve, miss, change-type,
 * notes, print, and the Actions-menu openers. OASIS autosave, save-pill, and
 * signature-invalidation-on-edit stay in the panel composition root.
 */
export function useVisitDetailActions({
  visitId,
  visit,
  isDeleted,
  isEditable,
  isMissedVisit,
  canBypassSignature,
  canSubmitWithoutSignature,
  canReturnToClinician,
  canViewWorkLog,
  canViewEvv,
  hasStagedAnswers,
  taskNotes,
  setTaskNotes,
  setVisit,
  setSignatures,
  setSaveError,
  onVisitUpdated,
  attestations,
}: UseVisitDetailActionsArgs): UseVisitDetailActionsResult {
  const { error: showError } = useToast();
  const { exportPdf, exportingId } = usePdfExport();
  const openDeferred = useDeferredDialogOpener();

  const [signingLoading, setSigningLoading] = useState(false);
  const [approvingLoading, setApprovingLoading] = useState(false);
  const [editingLoading, setEditingLoading] = useState(false);
  const [unsignedMoveLoading, setUnsignedMoveLoading] = useState(false);
  const [markMissedLoading, setMarkMissedLoading] = useState(false);
  const [unmarkMissedLoading, setUnmarkMissedLoading] = useState(false);
  const [changeTypeLoading, setChangeTypeLoading] = useState(false);
  const [taskNotesLoading, setTaskNotesLoading] = useState(false);

  const [signAttestationOpen, setSignAttestationOpen] = useState(false);
  const [approveAttestationOpen, setApproveAttestationOpen] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showMarkMissedDialog, setShowMarkMissedDialog] = useState(false);
  const [showChangeTypeDialog, setShowChangeTypeDialog] = useState(false);
  const [showTransitionNotesModal, setShowTransitionNotesModal] =
    useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [workLogOpen, setWorkLogOpen] = useState(false);
  const [evvOpen, setEvvOpen] = useState(false);
  const [hasEvv, setHasEvv] = useState(false);

  const [visitTypeOptions, setVisitTypeOptions] = useState<VisitTypeOption[]>(
    []
  );
  const [selectedVisitType, setSelectedVisitType] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [transitionNotes, setTransitionNotes] = useState('');
  const [pendingTransitionAction, setPendingTransitionAction] = useState<
    (() => Promise<void>) | null
  >(null);

  const visitAttestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(
      attestations,
      resolveAttestationFamily('visit', null, { isMissedVisit })
    );
  }, [attestations, isMissedVisit]);

  const refreshEvvIndicator = useCallback(() => {
    if (!visitId || !canViewEvv || isDeleted) {
      setHasEvv(false);
      return;
    }
    getVisitEvv({ path: { visit_id: visitId } })
      .then(({ data }) => {
        // A removed verification reads as "not verified" so the icon resets and
        // a new reading can be taken. Use has_evv_data which accounts for removed_at.
        setHasEvv(!!data?.has_evv_data);
      })
      .catch(() => {
        setHasEvv(false);
      });
  }, [visitId, canViewEvv, isDeleted]);

  useEffect(() => {
    refreshEvvIndicator();
  }, [refreshEvvIndicator]);

  const { actionItems: bypassActionItems, dialog: bypassDialog } =
    useSignatureBypassAction({
      taskId: visitId,
      canBypass: canBypassSignature,
      onError: setSaveError,
      onBypassed: async () => {
        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId } }),
          getTaskSignatures({ path: { task_id: visitId } }),
        ]);
        if (visitData?.data) {
          setVisit(visitData.data);
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
      },
    });

  const executeWithTransitionNotes = useCallback(
    (action: () => Promise<void>) => {
      setPendingTransitionAction(() => action);
      setTransitionNotes(taskNotes);
      setShowTransitionNotesModal(true);
    },
    [taskNotes]
  );

  const handleConfirmTransition = useCallback(async () => {
    if (!pendingTransitionAction || !visitId) return;

    if (transitionNotes !== taskNotes) {
      try {
        await updateTaskNotes({
          path: { task_id: visitId },
          body: { notes: transitionNotes || null },
        });
        setTaskNotes(transitionNotes);
      } catch (err) {
        console.error('Error saving notes:', err);
        setSaveError('Failed to save task notes.');
        setShowTransitionNotesModal(false);
        setPendingTransitionAction(null);
        return;
      }
    }

    setShowTransitionNotesModal(false);
    await pendingTransitionAction();
    setPendingTransitionAction(null);
  }, [
    pendingTransitionAction,
    visitId,
    transitionNotes,
    taskNotes,
    setTaskNotes,
    setSaveError,
  ]);

  const handleCancelTransition = useCallback(() => {
    setShowTransitionNotesModal(false);
    setPendingTransitionAction(null);
  }, []);

  const doSignAndSubmit = useCallback(
    async (
      credential: ESignatureCredentialInput,
      signatureDate?: SignatureDateInput
    ): Promise<string | null> => {
      if (!visitId || !visitAttestation) return null;

      try {
        setSigningLoading(true);
        setSaveError(null);

        const signResult = await signTask({
          path: { task_id: visitId },
          body: {
            ...credential,
            ...signatureDate,
            attestation_version: visitAttestation.version,
          },
        });

        if (signResult.error) {
          console.error('Error signing visit:', signResult.error);
          const message =
            getBusinessErrorMessage(signResult) ??
            'Failed to sign and submit. Please try again.';
          setSaveError(message);
          return message;
        }

        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId } }),
          getTaskSignatures({
            path: { task_id: visitId },
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
        setSignAttestationOpen(false);
        return null;
      } catch (err) {
        console.error('Error signing visit:', err);
        const message = 'Failed to sign and submit. Please try again.';
        setSaveError(message);
        return message;
      } finally {
        setSigningLoading(false);
      }
    },
    [
      visitId,
      visitAttestation,
      setSaveError,
      setVisit,
      onVisitUpdated,
      setSignatures,
    ]
  );

  const handleSignAndSubmit = useCallback(() => {
    setSignAttestationOpen(true);
  }, []);

  const doApprove = useCallback(
    async (credential: ESignatureCredentialInput): Promise<string | null> => {
      if (!visitId || !visitAttestation) return null;

      try {
        setApprovingLoading(true);
        setSaveError(null);

        const result = await approveTask({
          path: { task_id: visitId },
          body: {
            ...credential,
            attestation_version: visitAttestation.version,
          },
        });

        if (result.error) {
          console.error('Error completing visit:', result.error);
          const message =
            getBusinessErrorMessage(result) ??
            'Failed to complete visit. Please try again.';
          setSaveError(message);
          return message;
        }

        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId } }),
          getTaskSignatures({
            path: { task_id: visitId },
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
        setApproveAttestationOpen(false);
        return null;
      } catch (err) {
        console.error('Error completing visit:', err);
        const message = 'Failed to complete visit. Please try again.';
        setSaveError(message);
        return message;
      } finally {
        setApprovingLoading(false);
      }
    },
    [
      visitId,
      visitAttestation,
      setSaveError,
      setVisit,
      onVisitUpdated,
      setSignatures,
    ]
  );

  const handleApprove = useCallback(() => {
    // Transition notes are saved in handleConfirmTransition before this
    // callback runs; doApprove only needs the attestation dialog (no notes).
    executeWithTransitionNotes(async () => {
      setApproveAttestationOpen(true);
    });
  }, [executeWithTransitionNotes]);

  const refreshAfterUnsignedMove = useCallback(async () => {
    if (!visitId) return;
    const [visitData, signaturesData] = await Promise.all([
      getVisit({ path: { visit_id: visitId } }),
      getTaskSignatures({ path: { task_id: visitId } }),
    ]);
    if (visitData?.data) {
      setVisit(visitData.data);
      onVisitUpdated?.(visitData.data);
    }
    if (signaturesData?.data) {
      setSignatures(signaturesData.data);
    }
  }, [visitId, setVisit, onVisitUpdated, setSignatures]);

  const handleSubmitWithoutSignature = useCallback(async () => {
    if (!visitId) return;
    try {
      setUnsignedMoveLoading(true);
      setSaveError(null);
      const result = await submitTaskWithoutSignature({
        path: { task_id: visitId },
      });
      if (result.error) {
        console.error('Error submitting without signature:', result.error);
        setSaveError(
          getBusinessErrorMessage(result) ??
            'Failed to submit. Please try again.'
        );
        return;
      }
      await refreshAfterUnsignedMove();
    } catch (err) {
      console.error('Error submitting without signature:', err);
      setSaveError('Failed to submit. Please try again.');
    } finally {
      setUnsignedMoveLoading(false);
    }
  }, [visitId, setSaveError, refreshAfterUnsignedMove]);

  const handleReturnToClinician = useCallback(async () => {
    if (!visitId) return;
    try {
      setUnsignedMoveLoading(true);
      setSaveError(null);
      const result = await returnTaskToClinician({
        path: { task_id: visitId },
      });
      if (result.error) {
        console.error('Error returning to clinician:', result.error);
        setSaveError(
          getBusinessErrorMessage(result) ??
            'Failed to return the chart. Please try again.'
        );
        return;
      }
      await refreshAfterUnsignedMove();
    } catch (err) {
      console.error('Error returning to clinician:', err);
      setSaveError('Failed to return the chart. Please try again.');
    } finally {
      setUnsignedMoveLoading(false);
    }
  }, [visitId, setSaveError, refreshAfterUnsignedMove]);

  const handleBreakSignatures = useCallback(async () => {
    if (!visitId) return;

    const doBreak = async () => {
      try {
        setEditingLoading(true);
        setSaveError(null);

        const result = await breakTaskSignatures({
          path: { task_id: visitId },
        });

        if (result.error) {
          console.error('Error breaking signatures:', result.error);
          setSaveError('Failed to break signatures. Please try again.');
          return;
        }

        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId } }),
          getTaskSignatures({
            path: { task_id: visitId },
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
      } catch (err) {
        console.error('Error breaking signatures:', err);
        setSaveError('Failed to break signatures. Please try again.');
      } finally {
        setEditingLoading(false);
      }
    };

    executeWithTransitionNotes(doBreak);
  }, [
    visitId,
    setSaveError,
    setVisit,
    onVisitUpdated,
    setSignatures,
    executeWithTransitionNotes,
  ]);

  const handleMarkAsMissed = useCallback(
    async (payload: MissedVisitDataCreate) => {
      if (!visitId) return;
      try {
        setMarkMissedLoading(true);
        setSaveError(null);
        const result = await markVisitAsMissed({
          path: { visit_id: visitId },
          body: payload,
        });
        if (result.error) {
          console.error('Error marking visit as missed:', result.error);
          showError('Unable to mark visit as missed. Please try again.');
          return;
        }
        if (result.data) {
          setVisit(result.data);
          setSignatures([]);
          onVisitUpdated?.(result.data);
        }
        setShowMarkMissedDialog(false);
      } catch (err) {
        console.error('Error marking visit as missed:', err);
        showError('Unable to mark visit as missed. Please try again.');
      } finally {
        setMarkMissedLoading(false);
      }
    },
    [visitId, setSaveError, setVisit, setSignatures, onVisitUpdated, showError]
  );

  const handleUnmarkMissed = useCallback(async () => {
    if (!visitId) return;
    try {
      setUnmarkMissedLoading(true);
      setSaveError(null);
      const result = await unmarkVisitAsMissed({
        path: { visit_id: visitId },
      });
      if (result.error) {
        console.error('Error unmarking visit:', result.error);
        // This 409 is not retryable: another live SOC already holds the slot.
        showError(
          socConflictCopyFromError(result) ??
            'Unable to unmark visit. Please try again.'
        );
        return;
      }
      if (result.data) {
        setVisit(result.data);
        setSignatures([]);
        onVisitUpdated?.(result.data);
      }
    } catch (err) {
      console.error('Error unmarking visit:', err);
      showError(
        socConflictCopyFromError(err) ??
          'Unable to unmark visit. Please try again.'
      );
    } finally {
      setUnmarkMissedLoading(false);
    }
  }, [
    visitId,
    setSaveError,
    setVisit,
    setSignatures,
    onVisitUpdated,
    showError,
  ]);

  // The backend decides which aliases share this visit's form schema; an empty
  // list is what hides the action, so the two can never disagree about what is
  // switchable. Skipped for locked charts, which cannot be renamed anyway.
  useEffect(() => {
    if (!visitId || !isEditable) {
      setVisitTypeOptions([]);
      return;
    }
    let cancelled = false;
    const loadOptions = async () => {
      try {
        const result = await getVisitTypeChangeOptions({
          path: { visit_id: visitId },
        });
        if (!cancelled && result.data) setVisitTypeOptions(result.data);
      } catch (err) {
        // Non-fatal: the action stays hidden rather than blocking the chart.
        console.error('Error loading visit type options:', err);
      }
    };
    void loadOptions();
    return () => {
      cancelled = true;
    };
  }, [visitId, isEditable, visit?.visit_type]);

  const handleChangeVisitType = useCallback(async () => {
    if (!visitId || !selectedVisitType) return;
    try {
      setChangeTypeLoading(true);
      const result = await changeVisitType({
        path: { visit_id: visitId },
        body: { visit_type: selectedVisitType as VisitTypeId },
      });
      if (result.error) {
        console.error('Error changing visit type:', result.error);
        // The 409 body is the only error shape carrying a ConflictCode; a 422
        // has no `code` and falls through to the generic retry copy.
        const code = 'code' in result.error ? result.error.code : null;
        showError(changeVisitTypeErrorCopy(code));
        return;
      }
      if (result.data) {
        setVisit(result.data);
        onVisitUpdated?.(result.data);
      }
      setShowChangeTypeDialog(false);
    } catch (err) {
      console.error('Error changing visit type:', err);
      showError(changeVisitTypeErrorCopy(null));
    } finally {
      setChangeTypeLoading(false);
    }
  }, [visitId, selectedVisitType, setVisit, onVisitUpdated, showError]);

  const handleSaveNotes = useCallback(async () => {
    if (!visitId) return;
    if (draftNotes === taskNotes) {
      setShowNotesDialog(false);
      return;
    }
    try {
      setTaskNotesLoading(true);
      setSaveError(null);
      const res = await updateTaskNotes({
        path: { task_id: visitId },
        body: { notes: draftNotes || null },
      });
      setTaskNotes(draftNotes);
      // Reflect the backend body-edit auto-advance (Not Started ->
      // Saved) without refetching the whole chart.
      const nextStatus = res.data?.status;
      if (nextStatus) {
        setVisit(prev => (prev ? { ...prev, status: nextStatus } : prev));
      }
      setShowNotesDialog(false);
    } catch (err) {
      console.error('Error saving task notes:', err);
      setSaveError('Failed to save task notes.');
    } finally {
      setTaskNotesLoading(false);
    }
  }, [visitId, draftNotes, taskNotes, setTaskNotes, setSaveError, setVisit]);

  const handleExportPdf = useCallback(() => {
    if (!visitId) return;
    void exportPdf(
      visitId,
      async (): Promise<PdfSource | null> => {
        const response = await getVisitPdf({
          path: { visit_id: visitId },
          ...(isDeleted && { query: { include_deleted: true } }),
        });
        return response.error || !response.data
          ? null
          : {
              blob: new Blob([response.data as BlobPart], {
                type: 'application/pdf',
              }),
            };
      },
      'visit PDF'
    );
  }, [visitId, isDeleted, exportPdf]);

  const secondaryHeaderActions: TaskHeaderSecondaryAction[] = useMemo(
    () => [
      ...(isEditable && !isMissedVisit
        ? [
            {
              label: 'Mark as Missed',
              icon: <Ban className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => {
                openDeferred('markMissed', () => setShowMarkMissedDialog(true));
              },
            },
          ]
        : []),
      ...(isEditable && isMissedVisit
        ? [
            {
              label: unmarkMissedLoading ? 'Unmarking…' : 'Unmark as Missed',
              icon: <Undo2 className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => void handleUnmarkMissed(),
              disabled: unmarkMissedLoading,
            },
          ]
        : []),
      ...bypassActionItems,
      ...(canSubmitWithoutSignature
        ? [
            {
              label: unsignedMoveLoading
                ? 'Submitting…'
                : 'Submit without signing',
              icon: (
                <SendHorizontal className='h-4 w-4 mr-2' aria-hidden='true' />
              ),
              onSelect: () => void handleSubmitWithoutSignature(),
              // Same reason as the Sign buttons: submitting moves the visit on
              // from its saved answers, which exclude anything still staged.
              disabled: unsignedMoveLoading || hasStagedAnswers,
            },
          ]
        : []),
      ...(canReturnToClinician
        ? [
            {
              label: unsignedMoveLoading ? 'Returning…' : 'Return to clinician',
              icon: <Undo2 className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => void handleReturnToClinician(),
              disabled: unsignedMoveLoading,
            },
          ]
        : []),
      ...(isEditable && !isMissedVisit && visitTypeOptions.length > 0
        ? [
            {
              label: 'Change task type',
              icon: <Tag className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => {
                setSelectedVisitType('');
                openDeferred('changeType', () => setShowChangeTypeDialog(true));
              },
            },
          ]
        : []),
      // Offered on a deleted chart too, where the dialog is read-only. Editing is
      // withheld there like the rest of the editing, but this dialog is also the
      // only way into the episode's shared log — notes that belong to the whole
      // episode, not to the chart that was deleted — so withholding the action
      // would put them out of reach from a deleted visit entirely.
      {
        label: isDeleted ? 'View notes' : 'Notes',
        icon: <StickyNote className='h-4 w-4 mr-2' aria-hidden='true' />,
        onSelect: () => {
          setDraftNotes(taskNotes);
          setSaveError(null);
          openDeferred('notes', () => setShowNotesDialog(true));
        },
      },
      {
        label: exportingId === visitId ? 'Opening print view...' : 'Print view',
        icon:
          exportingId === visitId ? (
            <div
              className='animate-spin rounded-full h-3 w-3 mr-2 border-b-2 border-current'
              aria-hidden='true'
            />
          ) : (
            <Printer className='h-4 w-4 mr-2' aria-hidden='true' />
          ),
        onSelect: handleExportPdf,
        disabled: exportingId === visitId,
      },
      {
        label: 'View history',
        icon: <History className='h-4 w-4 mr-2' aria-hidden='true' />,
        onSelect: () => {
          openDeferred('history', () => setHistoryOpen(true));
        },
      },
      ...(canViewWorkLog
        ? [
            {
              label: 'Work log',
              icon: (
                <ClipboardList className='h-4 w-4 mr-2' aria-hidden='true' />
              ),
              onSelect: () => {
                openDeferred('workLog', () => setWorkLogOpen(true));
              },
            },
          ]
        : []),
      // Two independent reasons to withhold this one. EVV access is a permission,
      // and separately the readings route was not widened for deleted visits, so
      // offering it on a deleted chart would only produce an error.
      ...(canViewEvv && !isDeleted
        ? [
            {
              // EMR-325: read-only proof-of-visit captured in the nursing app. Always
              // offered to users with EVV access — the dialog itself shows an empty
              // state when nothing was captured, which is most visits today.
              label: 'Patient Signature',
              icon: (
                <MapPin
                  className={`h-4 w-4 mr-2 ${
                    hasEvv ? 'text-green-600' : 'text-muted-foreground'
                  }`}
                  aria-hidden='true'
                />
              ),
              onSelect: () => {
                openDeferred('evv', () => setEvvOpen(true));
              },
            },
          ]
        : []),
    ],
    [
      isEditable,
      isMissedVisit,
      openDeferred,
      unmarkMissedLoading,
      handleUnmarkMissed,
      bypassActionItems,
      canSubmitWithoutSignature,
      unsignedMoveLoading,
      handleSubmitWithoutSignature,
      hasStagedAnswers,
      canReturnToClinician,
      handleReturnToClinician,
      visitTypeOptions.length,
      isDeleted,
      taskNotes,
      setSaveError,
      exportingId,
      visitId,
      handleExportPdf,
      canViewWorkLog,
      canViewEvv,
      hasEvv,
    ]
  );

  return {
    visitAttestation,
    signingLoading,
    approvingLoading,
    editingLoading,
    unsignedMoveLoading,
    markMissedLoading,
    unmarkMissedLoading,
    changeTypeLoading,
    taskNotesLoading,
    exportingId,
    signAttestationOpen,
    setSignAttestationOpen,
    approveAttestationOpen,
    setApproveAttestationOpen,
    showNotesDialog,
    setShowNotesDialog,
    showMarkMissedDialog,
    setShowMarkMissedDialog,
    showChangeTypeDialog,
    setShowChangeTypeDialog,
    showTransitionNotesModal,
    historyOpen,
    setHistoryOpen,
    workLogOpen,
    setWorkLogOpen,
    evvOpen,
    setEvvOpen,
    hasEvv,
    refreshEvvIndicator,
    visitTypeOptions,
    selectedVisitType,
    setSelectedVisitType,
    draftNotes,
    setDraftNotes,
    transitionNotes,
    setTransitionNotes,
    handleSignAndSubmit,
    doSignAndSubmit,
    handleApprove,
    doApprove,
    handleBreakSignatures,
    handleSubmitWithoutSignature,
    handleReturnToClinician,
    handleMarkAsMissed,
    handleUnmarkMissed,
    handleChangeVisitType,
    handleExportPdf,
    handleSaveNotes,
    handleConfirmTransition,
    handleCancelTransition,
    bypassDialog,
    secondaryHeaderActions,
  };
}
